import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, UserX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errorMessage, toQuery } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDebounced, useOptions, useOrgRefs } from "@/hooks/useDirectory";
import { OptionPicker } from "@/components/common/OptionPicker";
import { PageHeader } from "@/components/common/Layout";
import { Field, FormSection } from "@/components/common/Detail";
import { LoadingGrid, LoginPrompt } from "@/components/common/States";
import { PersonAvatar } from "@/components/common/Visuals";
import { OrgPicker } from "@/components/persons/OrgPicker";
import { GroupPicker } from "@/components/persons/GroupPicker";
import { cn } from "@/lib/utils";

const EMPTY = {
  first_name: "", last_name: "", email: "", phone: "", job_title: "", linkedin: "", photo_url: "", business_category: "",
  membership: "", city: "", geo_department: "", organizations: [],
  business_department: [], profile_type: [], group_ids: [], expertises: [],
};
const TEXT_FIELDS = Object.keys(EMPTY).filter((k) => typeof EMPTY[k] === "string");
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function MiniPerson({ person }) {
  return (
    <Link to={`/personnes/${person.id}`} target="_blank" className="flex items-center gap-3 rounded-xl bg-white p-2.5 hover:shadow-sm" data-testid={`duplicate-person-${person.id}`}>
      <PersonAvatar person={person} size="sm" className="rounded-xl" />
      <div className="min-w-0 text-sm">
        <div className="truncate font-bold">{person.full_name}</div>
        <div className="truncate text-muted-foreground">{[person.job_title, person.organizations.map((o) => o.name).join(", "), person.email].filter(Boolean).join(" · ")}</div>
      </div>
    </Link>
  );
}

function DuplicateAlerts({ emailMatch, similar }) {
  if (emailMatch) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-5" data-testid="duplicate-email-alert">
        <div className="flex items-center gap-2 font-bold text-red-800"><UserX className="h-5 w-5" /> Ce contact existe déjà dans l'annuaire.</div>
        <p className="mt-1 text-sm text-red-700">Cette adresse email est déjà utilisée par le contact ci-dessous. Le doublon ne sera pas créé.</p>
        <div className="mt-3"><MiniPerson person={emailMatch} /></div>
        <Button asChild size="sm" className="mt-3 rounded-full bg-red-600 hover:bg-red-700" data-testid="duplicate-open-existing-button">
          <Link to={`/personnes/${emailMatch.id}`}>Ouvrir la fiche existante</Link>
        </Button>
      </div>
    );
  }
  if (!similar.length) return null;
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="similar-names-alert">
      <div className="flex items-center gap-2 font-bold text-amber-900"><AlertTriangle className="h-5 w-5" /> Des contacts au nom très proche existent déjà</div>
      <p className="mt-1 text-sm text-amber-800">Vérifiez qu'il ne s'agit pas de la même personne. Vous pouvez tout de même enregistrer.</p>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">{similar.map((p) => <MiniPerson key={p.id} person={p} />)}</div>
    </div>
  );
}

export default function PersonFormPage() {
  const { id } = useParams();
  const editing = Boolean(id);
  const { isAuthed, loading } = useAuth();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [initialPhoto, setInitialPhoto] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null);

  const person = useQuery({ queryKey: ["person", id, true], queryFn: async () => (await api.get(`/persons/${id}`)).data, enabled: editing && isAuthed });
  const options = useOptions(isAuthed);
  const prefillOrg = !editing && sp.get("org") ? [sp.get("org")] : [];
  const orgRefs = useOrgRefs(prefillOrg);

  useEffect(() => {
    const p = person.data;
    if (!p) return;
    const next = { ...EMPTY };
    TEXT_FIELDS.forEach((k) => { next[k] = p[k] || ""; });
    next.photo_url = p.photo || "";
    next.organizations = p.organizations.map(({ id: oid, name }) => ({ id: oid, name }));
    next.group_ids = p.group_ids || [];
    next.expertises = p.expertises || [];
    next.business_department = p.business_department || [];
    next.profile_type = p.profile_type || [];
    setForm(next);
    setInitialPhoto(p.photo || "");
  }, [person.data]);

  useEffect(() => {
    if (orgRefs.data?.length) setForm((f) => (f.organizations.length ? f : { ...f, organizations: orgRefs.data.map((o) => ({ id: o.id, name: o.name })) }));
  }, [orgRefs.data]);

  const dupKey = useDebounced(JSON.stringify([form.email.trim(), form.first_name.trim(), form.last_name.trim()]), 450);
  const dup = useQuery({
    queryKey: ["duplicates", dupKey, id],
    queryFn: async () => {
      const [email, first_name, last_name] = JSON.parse(dupKey);
      return (await api.get(`/persons/duplicates?${toQuery({ email: EMAIL_RE.test(email) ? email : "", first_name, last_name, exclude_id: id })}`)).data;
    },
    enabled: isAuthed && (!editing || !!person.data) && JSON.parse(dupKey).some(Boolean),
  });
  const emailMatch = conflict || dup.data?.email_match;

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
    if (key === "email") setConflict(null);
    if (errors[key]) setErrors((err) => ({ ...err, [key]: undefined }));
  };

  const validate = () => {
    const err = {};
    if (!form.first_name.trim()) err.first_name = "Le prénom est obligatoire.";
    if (!form.last_name.trim()) err.last_name = "Le nom est obligatoire.";
    if (!form.email.trim()) err.email = "L'email est obligatoire.";
    else if (!EMAIL_RE.test(form.email.trim())) err.email = "Adresse email invalide.";
    if (!form.organizations.length) err.organizations = "Choisissez ou créez l'entreprise.";
    setErrors(err);
    return !Object.keys(err).length;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return toast.error("Merci de compléter les champs obligatoires.");
    if (emailMatch) return toast.error("Ce contact existe déjà dans l'annuaire.");
    setSaving(true);
    const { organizations, ...payload } = form;
    payload.organization_ids = organizations.map((o) => o.id);
    if (editing && payload.photo_url === initialPhoto) delete payload.photo_url;
    try {
      const { data } = editing ? await api.patch(`/persons/${id}`, payload) : await api.post("/persons", payload);
      toast.success(editing ? "Fiche mise à jour dans Notion" : "Contact créé dans Notion");
      queryClient.invalidateQueries();
      navigate(`/personnes/${data.id}`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.existing) {
        setConflict(detail.existing);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;
  if (!isAuthed) return <div className="lm-container py-12"><LoginPrompt title="Ajout et modification réservés aux membres de La Mêlée" /></div>;
  if (editing && person.isLoading) return <div className="lm-container py-12"><LoadingGrid count={2} /></div>;

  const text = (key, props = {}) => (
    <Input id={key} value={form[key]} onChange={set(key)} data-testid={`person-form-${key.replace(/_/g, "-")}-input`}
      className={cn(errors[key] && "border-red-400 focus-visible:ring-red-300")} {...props} />
  );
  const o = options.data?.persons || {};
  const pick = (key, single = false) => <OptionPicker single={single} options={o[key]} value={form[key]} onChange={set(key)} testId={`person-form-${key.replace(/_/g, "-")}`} />;

  return (
    <form onSubmit={submit} className="lm-container max-w-4xl pb-32" data-testid="person-form" noValidate>
      <PageHeader eyebrow={editing ? "Modification" : "Nouveau contact"} title={editing ? `Modifier ${person.data?.full_name || ""}` : "Ajouter une personne"}
        description="Les informations sont enregistrées directement dans la base Notion Persona." />
      <div className="space-y-6">
        <DuplicateAlerts emailMatch={emailMatch} similar={emailMatch ? [] : dup.data?.similar || []} />
        <FormSection title="Identité">
          <Field label="Prénom" required htmlFor="first_name" error={errors.first_name}>{text("first_name", { autoComplete: "off" })}</Field>
          <Field label="Nom" required htmlFor="last_name" error={errors.last_name}>{text("last_name", { autoComplete: "off" })}</Field>
          <Field label="Email" required htmlFor="email" error={errors.email}>{text("email", { type: "email", autoComplete: "off" })}</Field>
          <Field label="Téléphone" htmlFor="phone">{text("phone", { type: "tel" })}</Field>
          <Field label="LinkedIn" htmlFor="linkedin">{text("linkedin", { placeholder: "https://www.linkedin.com/in/…" })}</Field>
          <Field label="Photo (URL)" htmlFor="photo_url">{text("photo_url", { placeholder: "https://…" })}</Field>
        </FormSection>
        <FormSection title="Organisation & fonction">
          <Field label="Entreprise" required htmlFor="organizations" error={errors.organizations} className="sm:col-span-2"
            hint="Tapez les premières lettres : si l'entreprise n'existe pas, vous pourrez la créer.">
            <OrgPicker value={form.organizations} onChange={set("organizations")} invalid={!!errors.organizations} />
          </Field>
          <Field label="Fonction" htmlFor="job_title" className="sm:col-span-2">{text("job_title", { placeholder: "ex : RSSI, Directrice des Systèmes d'Information…" })}</Field>
          <Field label="Catégorie métier" htmlFor="business_category" className="sm:col-span-2">{pick("business_category", true)}</Field>
          <Field label="Département métier" htmlFor="business_department" className="sm:col-span-2">{pick("business_department")}</Field>
        </FormSection>
        <FormSection title="Profil La Mêlée">
          <Field label="Adhésion" htmlFor="membership">{pick("membership", true)}</Field>
          <Field label="Ville" htmlFor="city">{text("city")}</Field>
          <Field label="Type de profil" htmlFor="profile_type" className="sm:col-span-2">{pick("profile_type")}</Field>
          <Field label="Département" htmlFor="geo_department" className="sm:col-span-2">{pick("geo_department", true)}</Field>
        </FormSection>
        <section className="lm-card space-y-6 p-6 sm:p-7">
          <div>
            <h2 className="text-lg font-bold">Clubs & Commissions</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">Cliquez pour rattacher ou détacher. Plusieurs choix possibles.</p>
          </div>
          <GroupPicker value={form.group_ids} onChange={set("group_ids")} testId="person-form-groups" />
        </section>
        <section className="lm-card space-y-4 p-6 sm:p-7">
          <h2 className="text-lg font-bold">Expertises</h2>
          {pick("expertises")}
        </section>
        {editing && (
          <section className="lm-card space-y-2 p-6 sm:p-7" data-testid="person-form-lists-hint">
            <h2 className="text-lg font-bold">Listes</h2>
            <p className="text-sm text-muted-foreground">L'ajout et le retrait dans les listes se font depuis la fiche de la personne (section « Listes ») ou depuis le répertoire.</p>
          </section>
        )}
      </div>
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-white/90 backdrop-blur-xl">
        <div className="lm-container flex max-w-4xl items-center justify-end gap-3 py-3">
          <Button type="button" variant="ghost" onClick={() => navigate(-1)} data-testid="person-form-cancel-button">Annuler</Button>
          <Button type="submit" disabled={saving || !!emailMatch} className="rounded-full px-6 font-semibold" data-testid={editing ? "save-person-submit-button" : "create-person-submit-button"}>
            {saving ? "Enregistrement…" : editing ? "Enregistrer les modifications" : "Créer le contact"}
          </Button>
        </div>
      </div>
    </form>
  );
}
